/* @flow */
import * as React from 'react';

import shallowequal from 'shallowequal';

import type {
  InstancePrice,
  ProductFeature as InstancePriceFeature,
} from '@gandi/redux-resource-price';
import { instances } from '@gandi/redux-resource-simplehosting';
import { Price, useTranslator } from '@gandi/translate';

import { Form, RadioButtonGroupField } from 'gandi.react-form';

import type { PresenterProps } from './Presenter';

import styles from './Form.scss';

type ButonGroupItem = {
  label: React.Node,
  value: string,
  footer?: React.Node,
  ...
};

// We'd like to get rid of features that are of type "boolean"
// (they have no value associated) and are not provided/available.
const shouldIncludeFeature = (feature: InstancePriceFeature): boolean => {
  // TODO We use a transition api right now, and "boolean" are in fact "strings"
  // with 'yes'/'no' values.
  // When the https://phabricator.corp.gandi.net/T13687 is resolved, then
  // we can remove it.
  if (feature.type === 'string' && feature.value === 'no') return false;
  if (feature.type === 'boolean' && feature.value === 'false') return false;
  return true;
};
// We'd like to display feature's `name` only when the feature is of type
// "boolean".
const shouldDisplayFeatureValue = (feature: InstancePriceFeature): boolean => {
  // TODO We use a transition api right now, and "boolean" are in fact "strings"
  // with 'yes'/'no' values.
  // When the https://phabricator.corp.gandi.net/T13687 is resolved, then
  // we can remove it.
  if (feature.type === 'string' && feature.value === 'yes') return false;
  if (feature.type === 'boolean' && feature.value === 'true') return false;
  return true;
};

function InstanceCreateForm(props: PresenterProps): React.MixedElement {
  const { __ } = useTranslator();

  const durationUnitLabel = { d: __('day'), m: __('month'), y: __('year') };

  const sizeTarget = {
    [instances.INSTANCE_SIZE_S_PLUS]: __(
      'For small websites with small traffic',
    ),
    [instances.INSTANCE_SIZE_M]: __('CMS, blogs, and apps with little traffic'),
    [instances.INSTANCE_SIZE_L]: __('E-commerce, websites with mild traffic'),
    [instances.INSTANCE_SIZE_XL]: __(
      'E-commerce, websites with moderate traffic',
    ),
    [instances.INSTANCE_SIZE_XXL]: __(
      'E-commerce, CMS, blogs, and applications with intensive traffic',
    ),
  };

  function notInSharedFeatures(feature: InstancePrice['features'][0]): boolean {
    return !props.instancePrices.sharedFeatures.find(item =>
      shallowequal(item, feature),
    );
  }

  function formatPrice(
    price: number,
    durationUnit: InstancePrice['durationUnit'],
    normalPrice?: number,
  ): React.MixedElement {
    const currency = props.currency;
    const unitLabel = durationUnitLabel[durationUnit];

    if (Number.isNaN(price) || !currency) {
      return <span>{__('Price unavailable')}</span>;
    }

    if (price === 0) {
      return (
        <span className={styles.formatPrice__rebatePrice}>{__('Free')}</span>
      );
    }

    if (!normalPrice) {
      return (
        <span>
          <Price currency={currency}>{price}</Price>/{unitLabel}
        </span>
      );
    }

    return (
      <span>
        <span className={styles.formatPrice__fullPrice}>
          <Price currency={currency}>{normalPrice || price}</Price>
        </span>
        <span>&nbsp;</span>
        <span className={styles.formatPrice__rebatePrice}>
          <Price currency={currency}>{price}</Price>/{unitLabel}
        </span>
      </span>
    );
  }

  function renderTrialSizeItem(price: InstancePrice): ButonGroupItem {
    const size = price.options.size;
    const label = price.options.sizeLabel;
    const duration = price.maxDuration;
    const { taxesIncluded } = props;

    const item: ButonGroupItem = {
      label: [
        __('Trial %(duration)s days', { duration }),
        <br key={`br-${size}`} />,
        <div className={styles.formatPrice__root} key={`div-${size}`}>
          {formatPrice(
            taxesIncluded ? price.priceAfterTaxes : price.priceBeforeTaxes,
            price.durationUnit,
            taxesIncluded
              ? price.normalPriceAfterTaxes
              : price.normalPriceBeforeTaxes,
          )}
        </div>,
      ],
      value: 'trial',
    };

    item.footer = (
      <ul className={styles.features}>
        <li>
          {__('Same features as pack "%(packName)s"', { packName: label })}
        </li>
        <li>{__('Resize for free during trial')}</li>
        <li>{__('No strings attached')}</li>
      </ul>
    );

    return item;
  }

  function renderSizeItem(price: InstancePrice): ButonGroupItem {
    const { taxesIncluded } = props;

    const size = price.options.size;
    const label = price.options.sizeLabel;
    // $FlowFixMe[invalid-computed-prop]
    const target = sizeTarget[size];

    const item: ButonGroupItem = {
      label: [
        label,
        <br key={`br-${size}`} />,
        <div className={styles.formatPrice__root} key={`div-${size}`}>
          {formatPrice(
            taxesIncluded ? price.priceAfterTaxes : price.priceBeforeTaxes,
            price.durationUnit,
            taxesIncluded
              ? price.normalPriceAfterTaxes
              : price.normalPriceBeforeTaxes,
          )}
        </div>,
      ],
      value: size,
    };

    item.footer = (
      <div className={styles.features}>
        {!!target && <p>{target}</p>}
        <ul>
          {price.features
            .filter(notInSharedFeatures)
            .filter(shouldIncludeFeature)
            // eslint-disable-next-line prefer-arrow-callback
            .map(function renderFeature(feature, idx) {
              /* eslint-disable react/no-array-index-key */
              // This is "safe" to use index as key, features are statics,
              // and therefore will never be reindexed
              return (
                <li key={`offer-feature-${idx}`}>
                  {shouldDisplayFeatureValue(feature)
                    ? `${
                        feature.value_label
                      } ${feature.name_label.toLowerCase()}`
                    : feature.name_label}
                </li>
              );
              /* eslint-enable react/no-array-index-key */
            })}
        </ul>
      </div>
    );

    return item;
  }

  function getSizeItems(): Array<ButonGroupItem> {
    const sizeItems = props.instancePrices.all.map(renderSizeItem);

    if (!('cross_sell' in props.location.query) && props.instancePrices.trial) {
      return [renderTrialSizeItem(props.instancePrices.trial)].concat(
        sizeItems,
      );
    }
    return sizeItems;
  }

  return (
    <Form
      id={props.formName}
      onSubmit={props.form.handleSubmit}
      className="form-stacked"
    >
      <RadioButtonGroupField
        full
        className={styles.radioButtonGroup}
        help={__(
          'Every instance comes with %(features)s, and unlimited bandwidth.',
          {
            features: props.instancePrices.sharedFeatures
              .map(feature =>
                shouldDisplayFeatureValue(feature)
                  ? `${feature.value_label} ${feature.name_label.toLowerCase()}`
                  : feature.name_label,
              )
              .join(','),
          },
        )}
        items={getSizeItems()}
        label={__('Size')}
        name="size"
        required
      />
    </Form>
  );
}

export default InstanceCreateForm;
